// Bespoke, dependency-free locale detection for the paywall + Stripe-checkout
// mini-localization project (Vietnamese + Brazilian Portuguese). Scoped to that
// flow only, using browser/device signals — no persisted user locale, no i18n
// library.
//
// See LOCALIZATION_SPEC.md for the eventual full-app localization plan.

export type PaywallLocale = "en" | "vi" | "pt-BR";

const DEBUG_LOCALE_STORAGE_KEY = "debugLocale";

function normalizeLocale(raw: string): PaywallLocale | null {
  const lower = raw.toLowerCase();
  if (lower.startsWith("vi")) return "vi";
  // Treat all Portuguese as Brazilian Portuguese for this mini-project — no
  // European Portuguese ("pt-PT") dictionary exists yet.
  if (lower.startsWith("pt")) return "pt-BR";
  return null;
}

function detectLocale(): PaywallLocale {
  // Dev-only override so you can QA a locale without touching browser/OS
  // settings. From the browser console: localStorage.setItem('debugLocale', 'vi')
  // then reload. Or in dev builds: setPaywallLocale('vi') / setPaywallLocale(null).
  try {
    const debugLocale = window.localStorage.getItem(DEBUG_LOCALE_STORAGE_KEY);
    if (debugLocale) {
      const normalized = normalizeLocale(debugLocale);
      if (normalized) return normalized;
    }
  } catch {
    // localStorage unavailable (private mode, SSR, etc.) — fall through.
  }

  const candidates =
    navigator.languages && navigator.languages.length > 0
      ? navigator.languages
      : [navigator.language];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const normalized = normalizeLocale(candidate);
    if (normalized) return normalized;
  }

  return "en";
}

export const paywallLocale: PaywallLocale =
  typeof navigator !== "undefined" ? detectLocale() : "en";

// Maps our paywall locale to a BCP 47 tag suitable for Intl/toLocaleDateString.
export function toIntlLocale(locale: PaywallLocale): string {
  switch (locale) {
    case "vi":
      return "vi-VN";
    case "pt-BR":
      return "pt-BR";
    default:
      return "en-US";
  }
}

export function setDebugPaywallLocale(locale: PaywallLocale | null) {
  if (locale) {
    window.localStorage.setItem(DEBUG_LOCALE_STORAGE_KEY, locale);
  } else {
    window.localStorage.removeItem(DEBUG_LOCALE_STORAGE_KEY);
  }
  window.location.reload();
}

// Dev convenience: `setPaywallLocale('vi')` / `setPaywallLocale(null)` from the
// browser console, no localStorage fiddling required.
if (import.meta.env.DEV && typeof window !== "undefined") {
  (window as any).setPaywallLocale = setDebugPaywallLocale;
}
